import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input } from 'reactstrap'

const SaveTemplateModal = ({ isOpen, toggle, onSave, elements }) => {
  const [templateName, setTemplateName] = useState('')
  const [description, setDescription] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [hasCustomElements, setHasCustomElements] = useState(false)

  // Check if element data should be preserved in templates
  const isCustomElement = (element) => {
    // Preserve elements with DB ID or specific types
    return !!element._id || 
           ['rich_text', 'heading', 'divider', 'spacer', 'image'].includes(element.type);
  }

  // Check if element may contain client-specific data
  const hasClientSpecificData = (elements) => {
    return elements.some(element => 
      !!element._id || ['rich_text', 'image'].includes(element.type)
    );
  }

  useEffect(() => {
    if (elements && elements.length) {
      setHasCustomElements(hasClientSpecificData(elements));
    }
  }, [elements]);

  const handleSave = async () => {
    if (!templateName.trim()) return
    
    setIsSaving(true)
    try {
      await onSave({
        name: templateName.trim(),
        description: description.trim()
      })
      setTemplateName('')
      setDescription('')
      toggle()
    } catch (error) {
      console.error('Error saving template:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    setTemplateName('')
    setDescription('')
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>Save as Template</ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label for="templateName">Template Name *</Label>
            <Input 
              id="templateName" 
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              placeholder="Enter template name"
              required
              disabled={isSaving}
              autoFocus
            />
          </FormGroup>
          <FormGroup>
            <Label for="description">Description (Optional)</Label>
            <Input 
              id="description"
              type="textarea"
              rows="3"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Enter template description"
              disabled={isSaving}
            />
          </FormGroup>
          
          {hasCustomElements && (
            <div className="alert alert-warning mt-3">
              <strong>Note:</strong> Custom elements, rich text, service agreements, and images will save client-specific data.
              You may need to update this information when reusing this template.
            </div>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button 
          color="primary" 
          onClick={handleSave} 
          disabled={!templateName.trim() || isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Template'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

SaveTemplateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  elements: PropTypes.array
}

export default SaveTemplateModal
